import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { createPaymentIntent } from '../services/stripe';
import { createOrder, clearCart as clearCartFirebase, getUserProfile } from '../services/database';
import { sendOrderEmails } from '../services/emailService';
import { CheckoutModal } from '../components/payment/CheckoutModal';
import { SuccessDialog } from '../components/common/SuccessDialog';
import { Order, OrderStatus } from '../types/order';
import { UserProfile } from '../types/user';

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
}

const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, clearCart, totalAmount, totalItems, generateOrderSummary } = useCart();
  const { currentUser, userProfile } = useAuth();

  const [customerInfoExpanded, setCustomerInfoExpanded] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: ''
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [orderId, setOrderId] = useState<string>('');

  // Pre-fill customer info when user profile loads
  useEffect(() => {
    if (userProfile) {
      setCustomerInfo({
        name: userProfile.name || '',
        email: userProfile.email || '',
        phone: userProfile.phone || ''
      });
    }
  }, [userProfile]);

  const handleQuantityDecrease = (productId: string, currentQty: number) => {
    if (currentQty > 1) {
      updateQuantity(productId, currentQty - 1);
    }
  };

  const handleQuantityIncrease = (productId: string, currentQty: number) => {
    updateQuantity(productId, currentQty + 1);
  };

  const handleRemoveItem = (productId: string, productName: string) => {
    if (window.confirm(`Remove "${productName}" from cart?`)) {
      removeItem(productId);
      toast.success('Item removed from cart', {
        position: 'top-center',
        autoClose: 2000,
      });
    }
  };

  const formatAddress = (address: UserProfile['shippingAddress']): string => {
    if (!address) return '';
    return `${address.street}, ${address.city}, ${address.state}, ${address.zipCode}, ${address.country}`;
  };

  const generateOrderId = (): string => {
    return `ORD-${Date.now()}-${Math.random().toString(36).substring(2, 9).toUpperCase()}`;
  };

  const handleCheckout = async () => {
    // Step 1: Check if user is logged in
    if (!currentUser) {
      toast.info('Please login or create an account to complete your purchase', {
        position: 'top-center',
        autoClose: 3000,
      });
      navigate('/login?redirect=/cart');
      return;
    }

    // Step 2: Check if user has shipping address
    const profile = await getUserProfile(currentUser.uid);
    if (!profile?.shippingAddress?.street || !profile?.shippingAddress?.city) {
      toast.error('Please complete your profile first', {
        position: 'top-center',
        autoClose: 4000,
      });
      navigate('/complete-profile?redirect=/cart');
      return;
    }

    // Step 3: Validate cart not empty
    if (items.length === 0) {
      toast.error('Your cart is empty', {
        position: 'top-center',
        autoClose: 3000,
      });
      return;
    }

    setCheckoutLoading(true);
    console.log('🛒 Starting checkout process...');

    try {
      // Step 4: Create Payment Intent
      console.log('💰 Creating payment intent for amount:', totalAmount);
      const paymentIntentResponse = await createPaymentIntent(
        totalAmount,
        customerInfo.email || profile.email,
        {
          order_items: items.map(item =>
            `${item.product.name} x${item.quantity}`
          ).join(', '),
          total_items: totalItems.toString(),
          customer_name: customerInfo.name || profile.name,
          customer_phone: customerInfo.phone || profile.phone || 'N/A',
        }
      );

      console.log('✅ Payment intent created:', paymentIntentResponse);

      // Handle both client_secret (snake_case) and clientSecret (camelCase)
      const clientSecretValue = paymentIntentResponse?.client_secret || paymentIntentResponse?.clientSecret;

      if (!clientSecretValue) {
        throw new Error('No client secret received from payment intent');
      }

      setClientSecret(clientSecretValue);
    } catch (error) {
      console.error('❌ Checkout error:', error);
      toast.error('Failed to initialize checkout. Please try again.', {
        position: 'top-center',
        autoClose: 4000,
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!currentUser || !userProfile) return;

    try {
      // Generate order ID
      const newOrderId = generateOrderId();
      setOrderId(newOrderId);

      // Create order in Firebase
      const order: Order = {
        id: newOrderId,
        userId: currentUser.uid,
        items: items,
        totalAmount: totalAmount,
        status: OrderStatus.PENDING,
        orderDate: new Date(),
        shippingAddress: formatAddress(userProfile.shippingAddress),
        customerName: customerInfo.name || userProfile.name,
        customerEmail: customerInfo.email || userProfile.email,
        customerPhone: customerInfo.phone || userProfile.phone || null,
        paymentIntentId: paymentIntentId,
      };

      await createOrder(currentUser.uid, order);

      // Send order notification emails
      console.log('📧 Payment successful, sending order emails...');
      const orderSummary = generateOrderSummary();

      try {
        const emailResult = await sendOrderEmails({
          orderSummary: orderSummary,
          customerName: customerInfo.name || userProfile.name,
          customerEmail: customerInfo.email || userProfile.email,
          customerPhone: customerInfo.phone || userProfile.phone || null,
          orderId: newOrderId,
          totalAmount: totalAmount,
          shippingAddress: formatAddress(userProfile.shippingAddress)
        });

        if (emailResult.businessEmailSent) {
          console.log('✅ Business notification email sent successfully');
        } else {
          console.log('⚠️ Business notification email failed');
        }

        if (emailResult.customerEmailSent) {
          console.log('✅ Customer confirmation email sent to:', customerInfo.email || userProfile.email);
        } else {
          console.log('⚠️ Customer confirmation email failed');
        }

        // Show toast notification about email status
        if (emailResult.customerEmailSent) {
          toast.success('Order confirmation email sent!', {
            position: 'top-center',
            autoClose: 3000,
          });
        } else if (emailResult.errors.length > 0) {
          toast.warning('Order placed but confirmation email may be delayed', {
            position: 'top-center',
            autoClose: 4000,
          });
        }
      } catch (emailError) {
        console.error('❌ Email sending error:', emailError);
        // Don't block order completion if email fails
        toast.warning('Order placed but confirmation email may be delayed', {
          position: 'top-center',
          autoClose: 4000,
        });
      }

      // Clear cart in Firebase and local state
      await clearCartFirebase(currentUser.uid);
      clearCart();

      // Close checkout modal
      setClientSecret(null);

      // Show success dialog
      setShowSuccessDialog(true);

    } catch (error) {
      toast.error('Failed to save order. Please contact support.', {
        position: 'top-center',
        autoClose: 5000,
      });
      console.error('Order save error:', error);
    }
  };

  const handleSuccessDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/home');
  };

  // Empty state
  if (items.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#FFFFFF',
          padding: '20px 16px 16px',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#B8860B',
            margin: 0
          }}>
            Cart
          </h1>
        </div>

        {/* Empty State */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px'
        }}>
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
            <circle cx="9" cy="21" r="1" />
            <circle cx="20" cy="21" r="1" />
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
          </svg>
          <p style={{
            fontSize: '18px',
            fontWeight: 600,
            color: '#6B7280',
            margin: '20px 0 32px'
          }}>
            Your cart is empty
          </p>
          <button
            onClick={() => navigate('/home')}
            style={{
              backgroundColor: '#B8860B',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '30px',
              padding: '14px 32px',
              fontSize: '16px',
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#9A7209';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#B8860B';
            }}
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'white',
      paddingBottom: '400px'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#FFFFFF',
        padding: '20px 16px 16px',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#B8860B',
          margin: 0
        }}>
          Cart
        </h1>
      </div>

      {/* Cart Items List */}
      <div style={{ padding: '16px' }}>
        {items.map((item) => {
          const itemTotal = (item.product.price || 0) * item.quantity;
          const needsOrdering = item.quantity > 2;

          return (
            <div
              key={item.product.id}
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '12px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                display: 'flex',
                gap: '12px'
              }}
            >
              {/* Product Image */}
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '8px',
                backgroundColor: 'white',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {item.product.images && item.product.images.length > 0 ? (
                  <img
                    src={item.product.images[0]}
                    alt={item.product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                )}
              </div>

              {/* Product Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#1A1A1A',
                  margin: '0 0 4px 0',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {item.product.name}
                </h3>
                <p style={{
                  fontSize: '12px',
                  color: '#9CA3AF',
                  margin: '0 0 4px 0'
                }}>
                  SKU: {item.product.sku || item.product.productId}
                </p>
                <p style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#10B981',
                  margin: '0 0 4px 0'
                }}>
                  €{(item.product.price || 0).toFixed(2)} each
                </p>
              </div>

              {/* Controls */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '8px'
              }}>
                {/* Quantity Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={() => handleQuantityDecrease(item.product.id, item.quantity)}
                    disabled={item.quantity === 1}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: item.quantity === 1 ? 'not-allowed' : 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      color: item.quantity === 1 ? '#9CA3AF' : '#B8860B'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" />
                    </svg>
                  </button>

                  <div style={{
                    backgroundColor: '#F3F4F6',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#1A1A1A',
                    minWidth: '40px',
                    textAlign: 'center'
                  }}>
                    {item.quantity}
                  </div>

                  <button
                    onClick={() => handleQuantityIncrease(item.product.id, item.quantity)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      color: '#B8860B'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                    </svg>
                  </button>

                  <button
                    onClick={() => handleRemoveItem(item.product.id, item.product.name)}
                    style={{
                      padding: '4px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginLeft: '4px'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#EF4444">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  </button>
                </div>

                {/* Item Total */}
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#1A1A1A'
                }}>
                  €{itemTotal.toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Section - Fixed */}
      <div className="cart-bottom-section">
        {/* Customer Information Section */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px'
        }}>
          {/* Header */}
          <button
            onClick={() => setCustomerInfoExpanded(!customerInfoExpanded)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer'
            }}
          >
            <span style={{
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif',
              color: '#1A1A1A'
            }}>
              Customer Information (Optional)
            </span>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#6B7280"
              strokeWidth="2"
              style={{
                transform: customerInfoExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>

          {/* Expanded Content */}
          {customerInfoExpanded && (
            <div style={{ paddingTop: '16px' }}>
              {/* Name */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#6B7280',
                  marginBottom: '6px'
                }}>
                  Name
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                  <input
                    type="text"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                    placeholder="Your name"
                    style={{
                      width: '100%',
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '12px 12px 12px 44px',
                      fontSize: '14px',
                      fontFamily: 'Montserrat, sans-serif',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#6B7280',
                  marginBottom: '6px'
                }}>
                  Email
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                    placeholder="your@email.com"
                    style={{
                      width: '100%',
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '12px 12px 12px 44px',
                      fontSize: '14px',
                      fontFamily: 'Montserrat, sans-serif',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  color: '#6B7280',
                  marginBottom: '6px'
                }}>
                  Phone
                </label>
                <div style={{ position: 'relative' }}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  >
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    placeholder="+353 XX XXX XXXX"
                    style={{
                      width: '100%',
                      backgroundColor: 'white',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '12px 12px 12px 44px',
                      fontSize: '14px',
                      fontFamily: 'Montserrat, sans-serif',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '12px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <span style={{
              fontSize: '14px',
              color: '#6B7280'
            }}>
              Total Items
            </span>
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1A1A1A'
            }}>
              {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </span>
          </div>
          <div style={{
            height: '1px',
            backgroundColor: '#E5E7EB',
            margin: '8px 0'
          }} />
          <div style={{
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <span style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1A1A1A'
            }}>
              Total Amount
            </span>
            <span style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#10B981'
            }}>
              €{totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Payment Button */}
        <button
          onClick={handleCheckout}
          disabled={checkoutLoading}
          style={{
            width: '100%',
            backgroundColor: checkoutLoading ? '#9CA3AF' : '#4CAF50',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '12px',
            padding: '16px',
            fontSize: '16px',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 'bold',
            cursor: checkoutLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '8px',
            transition: 'background-color 0.2s'
          }}
        >
          {checkoutLoading ? (
            'Processing...'
          ) : (
            <>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                <path d="M1 10h22" />
              </svg>
              Pay Now (€{totalAmount.toFixed(2)})
            </>
          )}
        </button>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px'
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span style={{
            fontSize: '12px',
            fontFamily: 'Montserrat, sans-serif',
            color: '#9CA3AF'
          }}>
            Secure payment powered by Stripe
          </span>
        </div>
      </div>

      {/* Checkout Modal */}
      {clientSecret && (
        <CheckoutModal
          clientSecret={clientSecret}
          onSuccess={handlePaymentSuccess}
          onClose={() => setClientSecret(null)}
          totalAmount={totalAmount}
        />
      )}

      {/* Success Dialog */}
      {showSuccessDialog && (
        <SuccessDialog
          title="Order Placed Successfully!"
          message="Thank you for your purchase! Your order has been confirmed and we're preparing it for delivery."
          buttonText="Continue Shopping"
          onClose={handleSuccessDialogClose}
          orderId={orderId}
          amount={totalAmount}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .cart-bottom-section {
          background-color: #F9FAFB;
          padding: 16px;
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 9;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .cart-bottom-section > div,
        .cart-bottom-section > button {
          width: 100%;
          max-width: 600px;
        }

        /* On desktop with sidebar, adjust bottom section */
        @media (min-width: 768px) {
          .cart-bottom-section {
            left: 280px;
          }
        }

        @media (min-width: 1200px) {
          .cart-bottom-section {
            left: 0;
            padding: 16px 24px;
          }
          
          .cart-bottom-section > div,
          .cart-bottom-section > button {
            max-width: 700px;
          }
        }
      `}</style>
    </div>
  );
};

export default CartPage;
